/**
 * Citizen Agenda Service
 * Handles voting on issues, spam protection, and aggregation
 */

// Issue tags available for voting
export const ISSUE_TAGS = [
  { id: "road", label: "ถนน", icon: "road" },
  { id: "water", label: "น้ำประปา", icon: "water" },
  { id: "electricity", label: "ไฟฟ้า", icon: "electricity" },
  { id: "garbage", label: "ขยะ", icon: "garbage" },
  { id: "flood", label: "น้ำท่วม/ระบายน้ำ", icon: "flood" },
  { id: "education", label: "การศึกษา", icon: "education" },
  { id: "health", label: "สุขภาพ", icon: "health" },
  { id: "safety", label: "ความปลอดภัย", icon: "safety" },
  { id: "park", label: "สวนสาธารณะ", icon: "park" },
  { id: "other", label: "อื่นๆ", icon: "other" },
] as const;

export type IssueTagId = (typeof ISSUE_TAGS)[number]["id"];

// Vote data structure
export interface CitizenVote {
  tambon_id: string;
  tambon_name: string;
  province: string;
  issue_tags: IssueTagId[]; // Can vote for multiple tags
  custom_comment?: string;
  browser_fingerprint: string;
  created_at: Date;
}

// Aggregated issue data
export interface IssueAggregation {
  tag_id: IssueTagId;
  label: string;
  icon: string;
  vote_count: number;
}

// API base URL
const API_URL = process.env.REACT_APP_API_URL || "";

// Local storage keys
const VOTE_STORAGE_KEY = "citizen_agenda_votes";
const FINGERPRINT_KEY = "citizen_agenda_fp";

/**
 * Generate a simple browser fingerprint for spam protection
 * This is not cryptographically secure but provides basic protection
 */
export const generateBrowserFingerprint = (): string => {
  // Check if we already have a fingerprint stored
  const storedFp = localStorage.getItem(FINGERPRINT_KEY);
  if (storedFp) {
    return storedFp;
  }

  // Generate new fingerprint from browser characteristics
  const components = [
    navigator.userAgent,
    navigator.language,
    window.screen.width,
    window.screen.height,
    window.screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 0,
    // Canvas fingerprint (simple version)
    (() => {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.textBaseline = "top";
          ctx.font = "14px Arial";
          ctx.fillText("OBT-NearMe-FP", 2, 2);
          return canvas.toDataURL().slice(-50);
        }
      } catch (e) {
        return "no-canvas";
      }
      return "no-ctx";
    })(),
  ];

  // Create a hash from components
  const fingerprint = btoa(components.join("|"))
    .replace(/[+/=]/g, "")
    .slice(0, 32);

  // Store for future use
  localStorage.setItem(FINGERPRINT_KEY, fingerprint);

  return fingerprint;
};

/**
 * Get stored votes from localStorage
 */
const getStoredVotes = (): Record<
  string,
  { timestamp: number; tags: string[] }
> => {
  try {
    const stored = localStorage.getItem(VOTE_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

/**
 * Save votes to localStorage
 */
const saveVotes = (
  votes: Record<string, { timestamp: number; tags: string[] }>
): void => {
  try {
    localStorage.setItem(VOTE_STORAGE_KEY, JSON.stringify(votes));
  } catch (e) {
    console.error("Failed to save votes to localStorage:", e);
  }
};

/**
 * Check if user has already voted for this tambon in the last 24 hours
 */
export const hasVotedRecently = (tambonId: string): boolean => {
  const votes = getStoredVotes();
  const vote = votes[tambonId];

  if (!vote) return false;

  const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
  return vote.timestamp > twentyFourHoursAgo;
};

/**
 * Get the time remaining until user can vote again (in hours/minutes)
 */
export const getTimeUntilCanVote = (
  tambonId: string
): { hours: number; minutes: number } | null => {
  const votes = getStoredVotes();
  const vote = votes[tambonId];

  if (!vote) return null;

  const twentyFourHoursAfterVote = vote.timestamp + 24 * 60 * 60 * 1000;
  const remaining = twentyFourHoursAfterVote - Date.now();

  if (remaining <= 0) return null;

  const hours = Math.floor(remaining / (60 * 60 * 1000));
  const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));

  return { hours, minutes };
};

/**
 * Get previously voted tags for this tambon (if voted recently)
 */
export const getPreviousVoteTags = (tambonId: string): string[] => {
  const votes = getStoredVotes();
  const vote = votes[tambonId];

  if (!vote || !hasVotedRecently(tambonId)) return [];

  return vote.tags || [];
};

/**
 * Submit a vote for citizen agenda
 */
export const submitVote = async (
  tambonId: string,
  tambonName: string,
  province: string,
  issueTags: IssueTagId[],
  customComment?: string
): Promise<{ success: boolean; message: string }> => {
  // Check if already voted
  if (hasVotedRecently(tambonId)) {
    const timeLeft = getTimeUntilCanVote(tambonId);
    return {
      success: false,
      message: timeLeft
        ? `คุณได้โหวตไปแล้ว กรุณารออีก ${timeLeft.hours} ชั่วโมง ${timeLeft.minutes} นาที`
        : "คุณได้โหวตไปแล้ววันนี้",
    };
  }

  if (issueTags.length === 0 && !customComment?.trim()) {
    return {
      success: false,
      message: "กรุณาเลือกประเด็นหรือพิมพ์ความคิดเห็น",
    };
  }

  const fingerprint = generateBrowserFingerprint();

  try {
    const response = await fetch(`${API_URL}/api/citizen-agenda/vote`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        tambon_id: tambonId,
        tambon_name: tambonName,
        province: province,
        issue_tags: issueTags,
        custom_comment: customComment?.trim() || null,
        browser_fingerprint: fingerprint,
      }),
    });

    const data = await response.json();

    if (data.success) {
      // Store vote in localStorage
      const votes = getStoredVotes();
      votes[tambonId] = {
        timestamp: Date.now(),
        tags: issueTags,
      };
      saveVotes(votes);

      return {
        success: true,
        message: "ขอบคุณสำหรับความคิดเห็นของคุณ!",
      };
    } else {
      return {
        success: false,
        message: data.message || "เกิดข้อผิดพลาด กรุณาลองใหม่",
      };
    }
  } catch (error) {
    console.error("Error submitting vote:", error);
    return {
      success: false,
      message: "ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้ กรุณาลองใหม่",
    };
  }
};

/**
 * Fetch top issues for a specific tambon
 */
export const fetchTopIssues = async (
  tambonId: string,
  limit: number = 5
): Promise<IssueAggregation[]> => {
  try {
    const response = await fetch(
      `${API_URL}/api/citizen-agenda/top-issues/${tambonId}?limit=${limit}`
    );

    const data = await response.json();

    if (data.success && data.data) {
      // Map the response to include labels and icons
      return data.data.map(
        (item: { tag_id: IssueTagId; vote_count: number }) => {
          const tagInfo = ISSUE_TAGS.find((t) => t.id === item.tag_id);
          return {
            tag_id: item.tag_id,
            label: tagInfo?.label || item.tag_id,
            icon: tagInfo?.icon || "📌",
            vote_count: item.vote_count,
          };
        }
      );
    }

    return [];
  } catch (error) {
    console.error("Error fetching top issues:", error);
    return [];
  }
};

/**
 * Fetch all comments for a tambon
 */
export const fetchComments = async (
  tambonId: string,
  limit: number = 20
): Promise<{ comment: string; created_at: string }[]> => {
  try {
    const response = await fetch(
      `${API_URL}/api/citizen-agenda/comments/${tambonId}?limit=${limit}`
    );

    const data = await response.json();

    if (data.success && data.data) {
      return data.data;
    }

    return [];
  } catch (error) {
    console.error("Error fetching comments:", error);
    return [];
  }
};

/**
 * Fetch national statistics
 */
export const fetchNationalStats = async (): Promise<{
  total_votes: number;
  total_municipalities: number;
  top_issues: IssueAggregation[];
  top_municipalities?: {
    id: string;
    name: string;
    province: string;
    vote_count: number;
  }[];
  recent_comments?: {
    custom_comment: string;
    tambon_name: string;
    province: string;
    issue_tags: string[];
    issue_labels: string[];
    created_at: string;
  }[];
} | null> => {
  try {
    const response = await fetch(
      `${API_URL}/api/citizen-agenda/national-stats`
    );
    const data = await response.json();

    if (data.success) {
      return {
        total_votes: data.total_votes,
        total_municipalities: data.total_municipalities || 0,
        top_issues: data.top_issues,
        top_municipalities: data.top_municipalities,
        recent_comments: data.recent_comments,
      };
    }
    return null;
  } catch (error) {
    console.error("Error fetching national stats:", error);
    return null;
  }
};
